import React from "react";
import { useDownloadManagerProvider } from "@jugl-web/domain-resources/files/providers/DownloadManagerProvider";
import { getFileSizeLabel } from "@jugl-web/utils/utils/files";
import { ChatMessagePayloadAttachment } from "@jugl-web/rest-api";
import { cx, useTranslations } from "@jugl-web/utils";
import { AttachmentPreview } from "@web-src/modules/chats/components/AttachmentPreview";
import { CircleProgressBar } from "./components/CircleProgressBar";
import { ReactComponent as RetryIcon } from "./assets/retry-icon.svg";
import { ReactComponent as DownloadIcon } from "./assets/download-icon.svg";
import { ReactComponent as DownloadIconOutgoing } from "./assets/download-icon-outgoing.svg";
import { useChatMessageBubble } from "../../../../providers/ChatMessageBubbleProvider/useChatMessageBubble";

export const FilePreviewTile: React.FC<{
  attachment: ChatMessagePayloadAttachment;
  isDownloadAvailable: boolean;
  onDownload: (attachment: ChatMessagePayloadAttachment) => void;
}> = ({ attachment, isDownloadAvailable, onDownload }) => {
  const { t } = useTranslations();
  const { files, cancel } = useDownloadManagerProvider();
  const { isOutgoing, isTusDownloadAvailable, getAttachmentUrls } =
    useChatMessageBubble();

  const downloadFile = files[attachment.uid];
  const isDownloading = downloadFile?.status === "in-progress";
  const isDownloadError = downloadFile?.status === "error";

  const fileSizeString = isDownloading
    ? `${getFileSizeLabel(
        attachment.size * (downloadFile.progress / 100)
      )} / ${getFileSizeLabel(attachment.size)}`
    : getFileSizeLabel(attachment.size, 2);

  return (
    <>
      <AttachmentPreview
        attachment={attachment}
        preview={getAttachmentUrls(attachment).preview}
        color={isOutgoing ? "white" : "primary"}
      />
      <div className="flex flex-1 grow flex-col gap-1.5 truncate">
        <span
          className={cx(
            "leading-2 truncate font-medium",
            isOutgoing ? "text-white" : "text-dark"
          )}
        >
          {attachment.name}
        </span>
        <span
          className={cx(
            "truncate text-sm",
            isOutgoing ? "text-primary-100" : "text-[#828282]",
            isDownloadError && "text-gradients-danger",
            { hidden: !isDownloadError && !attachment.size }
          )}
        >
          {isDownloadError
            ? t({
                id: "chats-page.failed-tap-to-retry",
                defaultMessage: "Failed. Tap to retry",
              })
            : fileSizeString}
        </span>
      </div>
      {isTusDownloadAvailable &&
        (isDownloading || isDownloadAvailable || isDownloadError) && (
          <div
            className={cx(
              "flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full",
              isOutgoing ? "bg-white" : "bg-dark-500",
              isDownloadError && "bg-gradients-danger"
            )}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (isDownloading) {
                cancel(attachment.uid);
              } else {
                onDownload(attachment);
              }
            }}
          >
            {isDownloading ? (
              <CircleProgressBar
                progress={downloadFile.progress}
                isOutgoing={isOutgoing}
              />
            ) : isDownloadError ? (
              <RetryIcon />
            ) : isDownloadAvailable ? (
              isOutgoing ? (
                <DownloadIconOutgoing />
              ) : (
                <DownloadIcon />
              )
            ) : null}
          </div>
        )}
    </>
  );
};
